"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { billingLockoutEq, BillingLockoutRpc, billingLockoutKeys } from "./billing-lockout-rpc-client";
import type { RecheckBillingAccessResponse } from "@/contexts/billing/core/presentation/rpc/billing-lockout-rpc";

/**
 * User-initiated "I've already paid — recheck access". Forces a live reconcile
 * against Stripe truth (no throttle), so a paid-up customer can unlock
 * immediately instead of waiting for a missed/delayed `invoice.paid` webhook.
 * On success invalidates the lockout queries so the (self-healing) gate refetches.
 */
export function useRecheckBillingAccess() {
  const queryClient = useQueryClient();
  const mutation = useMutation(billingLockoutEq.mutationOptions({
    mutationKey: ["billing", "lockout", "recheck-access"],
    mutationFn: (_input: void) => Effect.gen(function* () {
      const rpcClient = yield* RpcClient.make(BillingLockoutRpc);
      return yield* rpcClient.recheckBillingAccess();
    }).pipe(Effect.scoped),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: billingLockoutKeys.all
      });
    }
  }));
  return {
    mutate: () => mutation.mutate(undefined),
    isPending: mutation.isPending,
    data: mutation.data
  };
}
export type { RecheckBillingAccessResponse };